import { formatBytes } from '@/lib/metrics-api';
import type { Server } from '@/types/api';

export type BadgeTone = 'success' | 'warning' | 'danger' | 'muted';

/**
 * How the platform reaches root on this server, as a badge.
 *
 * Green when anything that needs root can run; amber when the account was checked and cannot get
 * there, which is something the customer can fix and the reason is beside it; grey when nobody has
 * checked yet.
 */
export function privilegeBadge(server: Server): { tone: BadgeTone; label: string } {
  if (server.can_act_as_root) {
    return { tone: 'success', label: server.privilege_label };
  }

  if (server.privilege === 'unknown') {
    return { tone: 'muted', label: server.privilege_label };
  }

  return { tone: 'warning', label: server.privilege_label };
}

/**
 * Why commands that need root cannot run here, or null.
 *
 * Only for an account that was actually checked: "not checked yet" is already said by the badge, and
 * repeating it as a warning under every new server would read as a fault.
 */
export function privilegeProblem(server: Server): string | null {
  if (server.can_act_as_root || server.privilege === 'unknown') {
    return null;
  }

  return server.privilege_problem;
}

/** CPU, memory and disk as the last successful check found them, in the order people ask. */
export function hardwareSummary(server: Server): string[] {
  const facts = server.facts;

  if (facts === null) {
    return [];
  }

  const parts: string[] = [];

  if (facts.cpu_cores !== null) {
    parts.push(`${facts.cpu_cores} ${facts.cpu_cores === 1 ? 'core' : 'cores'}`);
  }

  if (facts.memory_total_bytes !== null) {
    parts.push(`${formatBytes(facts.memory_total_bytes)} memory`);
  }

  if (facts.disk_total_bytes !== null) {
    parts.push(
      facts.disk_free_bytes !== null
        ? `${formatBytes(facts.disk_free_bytes)} free of ${formatBytes(facts.disk_total_bytes)} disk`
        : `${formatBytes(facts.disk_total_bytes)} disk`,
    );
  }

  return parts;
}
